const chevronBaseStyles = {
	content: '""',
	display: 'inline-block',
	marginLeft: '0.5rem',
	backgroundColor: 'primary.main',
	verticalAlign: 'middle',
};

const userInteractionChevronSmallStyles = {
	'::after': {
		width: '0.5rem',
		height: '0.625rem',
		clipPath: 'polygon(26% 0%, 11% 12%, 59% 50%, 11% 88%, 26% 100%, 88% 50%)',
		...chevronBaseStyles,
	},
};

const userInteractionChevronMediumStyles = {
	'::after': {
		width: '0.75rem',
		height: '1.25rem',
		maskImage:
			"url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='20' viewBox='0 0 12 20' fill='none'%3E%3Cpath d='M0 18.23L1.77 20L11.77 10L1.77 0L0 1.77L8.23 10L0 18.23Z' fill='black'/%3E%3C/svg%3E\")",
		maskSize: 'contain',
		maskRepeat: 'no-repeat',
		maskPosition: 'center',
		...chevronBaseStyles,
	},
};

const userInteractionChevronLargeStyles = {
	'::after': {
		width: '1.25rem',
		height: '2rem',
		maskImage:
			"url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='20' viewBox='0 0 12 20' fill='none'%3E%3Cpath d='M0 18.23L1.77 20L11.77 10L1.77 0L0 1.77L8.23 10L0 18.23Z' fill='black'/%3E%3C/svg%3E\")",
		maskSize: 'contain',
		maskRepeat: 'no-repeat',
		maskPosition: 'center',
		...chevronBaseStyles,
	},
};

const userInteractionCopyStyles = {
	ul: {
		listStyleType: 'none',
		margin: 0,
		paddingLeft: 0,
		textAlign: 'left',
		color: 'text.secondary',
		'li:first-child': { color: 'text.primary' },
		'li:nth-child(n+2)': {
			'> strong': {
				color: 'success.dark',
				fontWeight: 'normal',
			},
			'> em': {
				color: 'accent01.dark',
				fontStyle: 'normal',
			},
		},
	},
};

const userInteractionLiteCopyStyles = {
	ul: {
		listStyleType: 'none',
		margin: 0,
		paddingLeft: 0,
		textAlign: 'left',
		color: 'text.primary',
		li: { lineHeight: 1.5, paddingBottom: '0.25rem' },
		'li:first-child': {
			display: 'flex',
			justifyContent: 'space-between',
			alignItems: 'center',
			'> em': {
				fontStyle: 'normal',
				backgroundColor: 'backgrounds.white',
				padding: '0.125rem 0.5rem',
			},
		},
		'li:nth-child(n+2)': {
			color: 'text.secondary',
			'> strong': {
				color: 'success.dark',
				fontWeight: 'normal',
				'> em': {
					fontStyle: 'normal',
					fontWeight: 'bold',
					color: 'text.primary',
					fontSize: '1rem',
				},
			},
			'> em': {
				fontStyle: 'normal',
				fontWeight: 'bold',
			},
		},
	},
};

const userInteractionDisabledStyles = {
	'&.Mui-disabled': {
		'&.user-interaction-expansive-root': {
			borderColor: 'neutrals.disabled',
			ul: { color: 'text.disabled' },
			'li:first-child': { color: 'text.disabled' },
			'li:nth-child(n+2)': {
				'> strong': { color: 'text.disabled' },
				'> em': { color: 'text.disabled' },
			},
			'::after': { backgroundColor: 'neutrals.disabled' },
			svg: { color: 'neutrals.disabled' },
		},
		'&.user-interaction-expansive-lite-root': {
			backgroundColor: 'backgrounds.white',
			borderColor: 'neutrals.disabled',
			ul: {
				color: 'text.disabled',
				li: {
					color: 'text.disabled',
					h4: { color: 'text.disabled' },
				},
			},
			'li:first-child': {
				color: 'text.disabled',
				'> em': { color: 'text.disabled' },
			},
			'li:nth-child(n+2)': {
				'> strong': { color: 'text.disabled' },
			},
			'::after': { backgroundColor: 'neutrals.disabled' },
			svg: { color: 'neutrals.disabled' },
		},
	},
};

export const userInteractionExpansiveStyles = {
	display: 'flex',
	flexDirection: 'row',
	border: '0.0625rem solid',
	borderColor: 'neutrals.primary',
	padding: '0.5rem',
	borderRadius: '1rem',
	width: '100%',
	':hover': { borderColor: 'secondary.main' },
	'.MuiTypography-root': {
		flex: 1,
	},
	'.MuiSvgIcon-root + .MuiTypography-root': {
		paddingLeft: '0.5rem',
	},
	...userInteractionDisabledStyles,
};

export const userInteractionChevronLookupObj = {
	sm: userInteractionChevronSmallStyles,
	md: userInteractionChevronMediumStyles,
	lg: userInteractionChevronLargeStyles,
	nn: {},
};

export const userInteractionBackgroundThemeLookupObj = {
	light: {
		backgroundColor: 'backgrounds.light',
		borderColor: 'transparent',
	},
	neutral: {
		backgroundColor: 'backgrounds.neutral',
		borderColor: 'transparent',
	},
	default: {
		borderColor: userInteractionExpansiveStyles.borderColor,
	},
};

const userInteractionExpansionLookupObj = {
	lite: {
		...userInteractionExpansiveStyles,
		...userInteractionLiteCopyStyles,
	},
	default: {
		...userInteractionExpansiveStyles,
		...userInteractionCopyStyles,
	},
};

export const getUserInteractionExpansiveStyles = (contentVariant?: string, colorScheme?: string, chevron?: string) => ({
	[[
		'&.user-interaction-expansive-',
		contentVariant && contentVariant === 'default' ? '' : `${contentVariant || 'fallback'}-`,
		'root',
	].join('')]: {
		...userInteractionExpansionLookupObj[
			(contentVariant as keyof typeof userInteractionExpansionLookupObj) || 'default'
		],
		...userInteractionBackgroundThemeLookupObj[
			(colorScheme as keyof typeof userInteractionBackgroundThemeLookupObj) || 'default'
		],
		...(userInteractionChevronLookupObj?.[chevron as keyof typeof userInteractionChevronLookupObj]
			? userInteractionChevronLookupObj[chevron as keyof typeof userInteractionChevronLookupObj]
			: {}),
	},
});
